import { Stack, Radio, Select, Text, Box, NumberInput } from '@mantine/core';
import PickupScheduler from '../PickupScheduler';
import { addressLabel } from '../../utils/addressLabel';

export default function OrderAddressTab({
  fulfillmentMethod,
  setFulfillmentMethod,
  addresses,
  shippingAddressId,
  setShippingAddressId,
  billingAddresses,
  billingAddressId,
  setBillingAddressId,
  locations,
  holidays = [],
  pickupLocationId,
  setPickupLocationId,
  pickupTime,
  setPickupTime,
  pickupDate,
  setPickupDate,
  shippingOverride,
  setShippingOverride,
  autoShippingCost,
  t,
}) {
  const isPickup = fulfillmentMethod === 'pickup';
  const selectedLocation = locations.find((l) => l.id === pickupLocationId);
  const langCode = t.langCode || 'en';

  return (
    <Stack gap="md">
      <Radio.Group
        value={fulfillmentMethod}
        onChange={(val) => {
          setFulfillmentMethod(val);
          if (val === 'pickup') setShippingOverride('');
        }}
        label={t('takeOrderFulfillment', 'Fulfillment method')}
        required
        aria-required="true"
      >
        <Stack gap="xs" mt="xs">
          <Radio
            value="delivery"
            label={t('takeOrderDelivery', 'Delivery')}
            description={t('takeOrderDeliveryDesc', 'Shipping address is required. Shipping cost is computed from the discounted item subtotal.')}
          />
          <Radio
            value="pickup"
            label={t('takeOrderPickup', 'Pickup')}
            description={t('takeOrderPickupDesc', 'No shipping cost. A pickup location and time slot are required.')}
          />
        </Stack>
      </Radio.Group>

      {isPickup ? (
        <>
          <Box>
            <Select
              label={t('takeOrderPickupLocation', 'Pickup location')}
              placeholder={t('takeOrderPickupLocationPlaceholder', 'Select a location…')}
              data={locations.map((l) => ({ value: l.id, label: `${l.name} — ${l.address}` }))}
              value={pickupLocationId || null}
              onChange={(val) => {
                setPickupLocationId(val || '');
                setPickupTime('');
                setPickupDate('');
              }}
              searchable
              required
              aria-required="true"
            />
          </Box>

          {pickupLocationId && (
            <Box>
              <Text component="label" size="sm" fw={600} display="block" mb={4}>
                {t('choosePickupDate', 'Pick a date')}
              </Text>
              <PickupScheduler
                windows={selectedLocation?.times || []}
                holidays={holidays}
                pickupDate={pickupDate}
                setPickupDate={setPickupDate}
                pickupTime={pickupTime}
                setPickupTime={setPickupTime}
                t={t}
                langCode={langCode}
              />
            </Box>
          )}

          {billingAddresses.length === 0 ? (
            <Text size="sm" color="red" role="alert">
              {t('takeOrderNoBillingAddresses', 'This customer has no saved billing address. Add one before placing the order.')}
            </Text>
          ) : (
            <Box>
              <Text component="label" htmlFor="take-order-billing" size="sm" fw={600} display="block" mb={4}>
                {t('takeOrderBillingAddress', 'Billing address')}
              </Text>
              <Select
                id="take-order-billing"
                placeholder={t('takeOrderNoBillingPlaceholder', 'Select a billing address…')}
                data={billingAddresses.map((a) => ({ value: a.id, label: addressLabel(a) }))}
                value={billingAddressId || null}
                onChange={(val) => setBillingAddressId(val || '')}
                searchable
                required
                aria-required="true"
              />
            </Box>
          )}
        </>
      ) : (
        <>
          {addresses.length === 0 ? (
            <Text size="sm" color="red" role="alert">
              {t('takeOrderNoShippingAddresses', 'This customer has no saved shipping addresses. Delivery cannot be selected until they add one.')}
            </Text>
          ) : (
            <Box>
              <Select
                label={t('takeOrderShippingAddress', 'Shipping address')}
                placeholder={t('takeOrderShippingPlaceholder', 'Select an address…')}
                data={addresses.map((a) => ({ value: a.id, label: addressLabel(a) }))}
                value={shippingAddressId || null}
                onChange={(val) => setShippingAddressId(val || '')}
                searchable
                required
                aria-required="true"
              />
            </Box>
          )}

          {billingAddresses.length === 0 ? (
            <Text size="sm" color="red" role="alert">
              {t('takeOrderNoBillingAddresses', 'This customer has no saved billing address. Add one before placing the order.')}
            </Text>
          ) : (
            <Box>
              <Select
                label={t('takeOrderBillingAddress', 'Billing address')}
                placeholder={t('takeOrderNoBillingPlaceholder', 'Select a billing address…')}
                data={billingAddresses.map((a) => ({ value: a.id, label: addressLabel(a) }))}
                value={billingAddressId || null}
                onChange={(val) => setBillingAddressId(val || '')}
                searchable
                required
                aria-required="true"
              />
            </Box>
          )}

          <Box>
            <NumberInput
              label={t('takeOrderShippingOverride', 'Shipping cost (CHF, optional)')}
              description={t('takeOrderShippingOverrideHint', 'Leave empty to auto-calculate from the discounted subtotal (currently CHF {amount}). Set 0 for free shipping.', { amount: Number(autoShippingCost || 0).toFixed(2) })}
              value={shippingOverride}
              onChange={(val) => setShippingOverride(val === '' ? '' : String(val))}
              min={0}
              decimalScale={2}
              allowNegative={false}
              hideControls
              styles={{ input: { maxWidth: 260 } }}
            />
          </Box>
        </>
      )}
    </Stack>
  );
}
